/**
 * RESPONSIBILITY: play a sermon's YouTube video in place of its thumbnail, in one click.
 *
 * A link to a channel or playlist has no video id to embed — youtubeId() returns '' — so
 * those still open in a new tab rather than embedding nothing. Only a link to an actual
 * video swaps the thumbnail for a playing iframe.
 */
import { useState, type ReactNode } from 'react';
import { youtubeId } from './youtube';

export function VideoPlayer({
  url, title, picture, className = 'video',
}: {
  url: string | undefined;
  title: string;
  picture: ReactNode;
  className?: string;
}) {
  const [playing, setPlaying] = useState(false);
  const id = youtubeId(url);

  if (playing && id) {
    return (
      <div className={className}>
        <iframe
          className="video-frame"
          src={`https://www.youtube.com/embed/${id}?autoplay=1`}
          title={title}
          allow="autoplay; encrypted-media; picture-in-picture"
          allowFullScreen
        />
      </div>
    );
  }

  if (id) {
    return (
      <button
        type="button"
        className={className}
        aria-label={title}
        onClick={() => setPlaying(true)}
      >
        {picture}
        <span className="video-play">&#9654;</span>
      </button>
    );
  }

  return (
    <a className={className} href={url || '#'} aria-label={title}>
      {picture}
      <span className="video-play">&#9654;</span>
    </a>
  );
}
